import { enumLabel } from '../../utils/enumLabels';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Dropdown,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Typography,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  HistoryOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { patientApi, Patient } from '../../api/patientApi';
import dermatoApi, {
  DermatoAuditEntry,
  DermatoForm,
  DermatoFormProgress,
  FormStatus,
} from '../../api/dermatoApi';
import { useDocumentLanguage } from '../../hooks/useDocumentLanguage';
import { DOCUMENT_LANGUAGE_LABELS } from '../../hooks/useLanguage';
import { canUseDermato, canUseHepato } from '../../utils/flowAccess';
import { useAuthStore } from '../../store/authStore';
import { features } from '../../config/features';
import HepatoRegistryEntry from './hepato-workspace/HepatoRegistryEntry';
import DermatoRegistryEntry from './dermato-workspace/DermatoRegistryEntry';

const { Title, Text } = Typography;

const calculateAge = (dateOfBirth: string): number | undefined => {
  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return undefined;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age -= 1;
  return age;
};

const STATUS_META: Record<FormStatus, { label: string; color: string }> = {
  [FormStatus.DRAFT]: { label: 'Ciornă', color: 'blue' },
  [FormStatus.SUBMITTED]: { label: 'Trimis spre revizuire', color: 'gold' },
  [FormStatus.REVIEWED]: { label: 'Revizuit', color: 'green' },
  [FormStatus.ARCHIVED]: { label: 'Arhivat', color: 'default' },
};

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'Formular creat',
  UPDATE: 'Date formular actualizate',
  SUBMIT: 'Trimis spre revizuire',
  REVIEW: 'Revizuit de specialist',
  DELETE: 'Formular șters',
  CREATE_LESION: 'Leziune creată',
  UPDATE_LESION: 'Leziune actualizată',
  DELETE_LESION: 'Leziune ștearsă',
  CREATE_ABCDE: 'Evaluare ABCDE creată',
  UPDATE_ABCDE: 'Evaluare ABCDE actualizată',
  DELETE_ABCDE: 'Evaluare ABCDE ștearsă',
  CREATE_DERMATOSCOPY: 'Dermatoscopie creată',
  UPDATE_DERMATOSCOPY: 'Dermatoscopie actualizată',
  DELETE_DERMATOSCOPY: 'Dermatoscopie ștearsă',
  CREATE_VIDEO_AI: 'Analiză video-AI creată',
  UPDATE_VIDEO_AI: 'Analiză video-AI actualizată',
  DELETE_VIDEO_AI: 'Analiză video-AI ștearsă',
  CREATE_TOTAL_BODY: 'Sesiune Total Body creată',
  UPDATE_TOTAL_BODY: 'Sesiune Total Body actualizată',
  DELETE_TOTAL_BODY: 'Sesiune Total Body ștearsă',
  CREATE_BIOPSY: 'Biopsie creată',
  UPDATE_BIOPSY: 'Biopsie actualizată',
  DELETE_BIOPSY: 'Biopsie ștearsă',
  CREATE_HISTOPATHOLOGY: 'Rezultat histopatologie creat',
  UPDATE_HISTOPATHOLOGY: 'Rezultat histopatologie actualizat',
  DELETE_HISTOPATHOLOGY: 'Rezultat histopatologie șters',
  CREATE_DIAGNOSIS: 'Diagnostic creat',
  UPDATE_DIAGNOSIS: 'Diagnostic actualizat',
  DELETE_DIAGNOSIS: 'Diagnostic șters',
  CREATE_TREATMENT: 'Plan de tratament creat',
  UPDATE_TREATMENT: 'Plan de tratament actualizat',
  DELETE_TREATMENT: 'Plan de tratament șters',
  CREATE_SURGERY: 'Intervenție chirurgicală creată',
  UPDATE_SURGERY: 'Intervenție chirurgicală actualizată',
  DELETE_SURGERY: 'Intervenție chirurgicală ștearsă',
  CREATE_SURGICAL_HISTOPATHOLOGY: 'HP piesă operatorie creată',
  UPDATE_SURGICAL_HISTOPATHOLOGY: 'HP piesă operatorie actualizată',
  DELETE_SURGICAL_HISTOPATHOLOGY: 'HP piesă operatorie ștearsă',
  CREATE_FOLLOWUP: 'Vizită follow-up creată',
  UPDATE_FOLLOWUP: 'Vizită follow-up actualizată',
  DELETE_FOLLOWUP: 'Vizită follow-up ștearsă',
  CREATE_OUTCOME: 'Outcome creat',
  UPDATE_OUTCOME: 'Outcome actualizat',
  DELETE_OUTCOME: 'Outcome șters',
};

const auditColor = (action: string): string => {
  if (action.startsWith('CREATE')) return 'green';
  if (action.startsWith('DELETE')) return 'red';
  if (action === 'SUBMIT') return 'blue';
  if (action === 'REVIEW') return 'gold';
  return 'gray';
};

const auditDetails = (entry: DermatoAuditEntry): string => {
  const parts: string[] = [];
  if (entry.changes && Object.keys(entry.changes).length > 0) {
    parts.push(`Câmpuri: ${Object.keys(entry.changes).join(', ')}`);
  }
  const clinicalCode = entry.metadata?.['clinicalCode'];
  if (typeof clinicalCode === 'string' && clinicalCode) {
    parts.push(`Leziune: ${clinicalCode}`);
  }
  return parts.join(' · ');
};

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const PatientDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { language } = useDocumentLanguage();
  const roleName = useAuthStore((state) => state.user?.role?.name);
  const allowDermato = canUseDermato(roleName);
  const allowHepato = canUseHepato(roleName);

  const [patient, setPatient] = useState<Patient | null>(null);
  const [forms, setForms] = useState<DermatoForm[]>([]);
  const [progressMap, setProgressMap] = useState<Record<string, DermatoFormProgress>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const [auditFormId, setAuditFormId] = useState<string | null>(null);
  const [auditEntries, setAuditEntries] = useState<DermatoAuditEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const p = await patientApi.getById(id);
      setPatient(p);
      const list = await dermatoApi.list({
        patientId: id,
        limit: 50,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
      });
      setForms(list.data);
      // Progresul real per formular (pentru „x/15 pași" și reluarea din pasul corect).
      const entries = await Promise.all(
        list.data.map(async (f) => {
          const progress = await dermatoApi.getProgress(f.id).catch(() => null);
          return progress ? ([f.id, progress] as const) : null;
        })
      );
      const map: Record<string, DermatoFormProgress> = {};
      for (const e of entries) {
        if (e) map[e[0]] = e[1];
      }
      setProgressMap(map);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? e?.message ?? 'Eroare la încărcarea pacientului');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const continueForm = (form: DermatoForm) => {
    // Reluare din primul pas incomplet (limitat la pasul 3 când nu există leziuni).
    const progress = progressMap[form.id];
    let targetIndex = progress?.steps.findIndex((s) => !s) ?? 0;
    if (targetIndex < 0) targetIndex = 0;
    if (progress && !progress.hasLesions) {
      targetIndex = Math.min(targetIndex, 2);
    }
    navigate(`/patients/${id}/dermato-wizard/${targetIndex + 1}`, {
      state: { editFormId: form.id },
    });
  };

  const handlePdf = async (form: DermatoForm, kind: 'anamnesis' | 'full') => {
    try {
      setPdfLoading(`${form.id}:${kind}`);
      const blob =
        kind === 'anamnesis'
          ? await dermatoApi.downloadAnamnesisPdf(form.id, language)
          : await dermatoApi.downloadFullPdf(form.id, language);
      const dateStr = new Date().toISOString().split('T')[0];
      downloadBlob(
        blob,
        `HEPASKIN_${kind === 'full' ? 'Complet' : 'Anamneza'}_${DOCUMENT_LANGUAGE_LABELS[language].short}_${dateStr}.pdf`
      );
      message.success('PDF descărcat');
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? 'Eroare la descărcarea PDF-ului');
    } finally {
      setPdfLoading(null);
    }
  };

  const openAudit = async (form: DermatoForm) => {
    setAuditFormId(form.id);
    setAuditEntries([]);
    try {
      setAuditLoading(true);
      const entries = await dermatoApi.getAuditTrail(form.id);
      setAuditEntries(entries);
    } catch {
      // interceptorul global afișează eroarea
    } finally {
      setAuditLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">Se încarcă datele pacientului…</Text>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" showIcon message="Pacientul nu a putut fi încărcat" description={error ?? 'Negăsit'} />
        <Button style={{ marginTop: 16 }} icon={<ArrowLeftOutlined />} onClick={() => navigate('/patients')}>
          Înapoi la pacienți
        </Button>
      </div>
    );
  }

  const age = calculateAge(patient.dateOfBirth);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Space align="center">
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/patients')}>
            Pacienți
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            {patient.lastName} {patient.firstName}
          </Title>
          <Tag color={patient.status === 'ACTIVE' ? 'green' : 'default'}>{enumLabel(patient.status)}</Tag>
        </Space>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void load()}>
            Reîncarcă
          </Button>
          {features.clinicalWorkflow && allowDermato && patient.programType !== 'HEPATO' && (
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => navigate(`/patients/${patient.id}/dermato-wizard`)}
            >
              Deschide wizard-ul dermato
            </Button>
          )}
          {features.clinicalWorkflow && allowHepato && patient.programType !== 'DERMATO' && (
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={() => navigate(`/patients/${patient.id}/hepato-wizard`)}
              data-testid="open-hepato-wizard"
            >
              Deschide wizard-ul hepato
            </Button>
          )}
        </Space>
      </div>

      <Card title="Date pacient" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
          <Descriptions.Item label="CNP">{patient.cnp}</Descriptions.Item>
          <Descriptions.Item label="Data nașterii">
            {new Date(patient.dateOfBirth).toLocaleDateString('ro-RO')}
            {age !== undefined ? ` (${age} ani)` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Sex">
            {patient.sex === 'M' ? 'Masculin' : patient.sex === 'F' ? 'Feminin' : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Program">{enumLabel(patient.programType)}</Descriptions.Item>
          <Descriptions.Item label="Telefon">{patient.phone ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="E-mail">{patient.email ?? '—'}</Descriptions.Item>
          <Descriptions.Item label="Localitate">
            {[patient.city, patient.county].filter(Boolean).join(', ') || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Înregistrat la">
            {new Date(patient.createdAt).toLocaleDateString('ro-RO')}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Registrul (D-01): datele importate din documente și validate */}
      {patient.programType !== 'HEPATO' && <DermatoRegistryEntry patientId={patient.id} />}
      {patient.programType !== 'DERMATO' && <HepatoRegistryEntry patientId={patient.id} />}

      {/* Faza 2: formularele wizard-ului (pași, PDF, cronologie) doar cu fluxul clinic activ */}
      {features.clinicalWorkflow && (
      <Card title={`Formulare dermatologice (${forms.length})`}>
        <Table
          scroll={{ x: 'max-content' }}
          rowKey="id"
          dataSource={forms}
          pagination={false}
          locale={{ emptyText: 'Niciun formular dermato pentru acest pacient' }}
          columns={[
            {
              title: 'Formular',
              key: 'id',
              width: 110,
              render: (_: unknown, f: DermatoForm) => (
                <Text code>{f.id.slice(0, 8)}</Text>
              ),
            },
            {
              title: 'Status',
              key: 'status',
              render: (_: unknown, f: DermatoForm) => {
                const meta = STATUS_META[f.status] ?? { label: f.status, color: 'default' };
                const progress = progressMap[f.id];
                return (
                  <Space size={8} wrap>
                    <Tag color={meta.color}>{meta.label}</Tag>
                    {f.status === FormStatus.DRAFT && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {progress ? `${progress.completedCount}/15 pași completați` : '…'}
                      </Text>
                    )}
                  </Space>
                );
              },
            },
            {
              title: 'Leziuni',
              dataIndex: 'lesionCount',
              key: 'lesionCount',
              width: 90,
              align: 'center',
            },
            {
              title: 'Creat',
              dataIndex: 'createdAt',
              key: 'createdAt',
              width: 130,
              render: (v: string) => new Date(v).toLocaleDateString('ro-RO'),
            },
            {
              title: 'Actualizat',
              dataIndex: 'updatedAt',
              key: 'updatedAt',
              width: 160,
              render: (v: string) => new Date(v).toLocaleString('ro-RO'),
            },
            {
              title: 'Acțiuni',
              key: 'actions',
              render: (_: unknown, f: DermatoForm) => (
                <Space wrap>
                  {f.status === FormStatus.DRAFT && allowDermato && features.clinicalWorkflow && (
                    <Button size="small" type="primary" icon={<PlayCircleOutlined />} onClick={() => continueForm(f)}>
                      Continuă
                    </Button>
                  )}
                  <Dropdown
                    menu={{
                      items: [
                        { key: 'anamnesis', label: 'Anamneză (secțiunile I-VI)' },
                        { key: 'full', label: 'Complet (secțiunile I-XIII)' },
                      ],
                      onClick: ({ key }) => void handlePdf(f, key as 'anamnesis' | 'full'),
                    }}
                  >
                    <Button size="small" icon={<DownloadOutlined />} loading={pdfLoading?.startsWith(f.id)}>
                      PDF ({DOCUMENT_LANGUAGE_LABELS[language].short})
                    </Button>
                  </Dropdown>
                  <Button size="small" icon={<HistoryOutlined />} onClick={() => void openAudit(f)}>
                    Istoric
                  </Button>
                  {allowDermato && features.clinicalWorkflow && (
                    <Button
                      size="small"
                      icon={<ClockCircleOutlined />}
                      onClick={() => navigate(`/dermato/forms/${f.id}/timeline`)}
                    >
                      Cronologie
                    </Button>
                  )}
                </Space>
              ),
            },
          ]}
        />
      </Card>
      )}

      <Modal
        open={auditFormId !== null}
        title={`Istoric formular ${auditFormId?.slice(0, 8) ?? ''}`}
        footer={null}
        width={640}
        onCancel={() => setAuditFormId(null)}
      >
        {auditLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin />
          </div>
        ) : auditEntries.length === 0 ? (
          <Text type="secondary">Nicio acțiune înregistrată.</Text>
        ) : (
          <div style={{ maxHeight: 480, overflowY: 'auto', paddingTop: 8 }}>
            <Timeline
              items={auditEntries.map((entry) => ({
                color: auditColor(entry.action),
                children: (
                  <div>
                    <div>
                      <Text strong>{ACTION_LABELS[entry.action] ?? entry.action}</Text>
                    </div>
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(entry.createdAt).toLocaleString('ro-RO')} — {entry.userName}
                      </Text>
                    </div>
                    {auditDetails(entry) && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {auditDetails(entry)}
                        </Text>
                      </div>
                    )}
                  </div>
                ),
              }))}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PatientDetailPage;
